/**
 * Display formatters - currency, dates, durations.
 *
 * Money is held internally as integer cents (BIGINT on the DB,
 * `number` in JS up to 2^53 cents = $90 trillion - plenty). All
 * UI rendering goes through ``formatCents`` so we never accidentally
 * drift to floating-point dollars in display.
 */

import i18n from "../i18n";

/** Format an integer-cents amount as a localised currency string. */
export function formatCents(cents: number, currency = "USD"): string {
  const amount = cents / 100;
  try {
    return new Intl.NumberFormat(i18n.language || undefined, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

/** Coerce a backend-emitted timestamp string into a Date that JavaScript
 *  parses as UTC.
 *
 *  The Aurora Data API returns TIMESTAMPTZ values as bare strings like
 *  "2026-05-02 04:53:00.123456" - no Z, no ``+00:00``. PG normalises
 *  the value to UTC before serialising, but without the marker JS's
 *  ``new Date(s)`` parses it as LOCAL TIME and the cashier sees a
 *  4-hour-off (or whatever their offset is) timestamp displayed back
 *  to them. We rebuild the string into ISO-8601-with-Z here so every
 *  formatter below treats the value as UTC and ``timeZone`` projects
 *  it correctly into the location's tz.
 *
 *  Callers that pass a real ``Date`` object skip this entirely. */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  let s = value.trim();
  // ISO 8601 wants T between date and time; PG emits a space.
  s = s.replace(" ", "T");
  // Append Z when no timezone marker is present. Match Z, +HH:MM,
  // +HHMM, +HH at the tail; absent any of those, treat as UTC.
  if (!/(Z|[+-]\d{2}:?\d{0,2})$/.test(s)) {
    s += "Z";
  }
  return new Date(s);
}

/** Localised short time - "2:42 PM" / "14:42".
 *  Pass ``tz`` (IANA, e.g. "America/Los_Angeles") to render in the
 *  location's timezone instead of the browser's. (#142) */
export function formatTime(value: string | Date, tz?: string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(i18n.language || undefined, {
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });
}

/** Localised short date-time - "Apr 25, 2:42 PM".
 *  Pass ``tz`` to render in the location's timezone. (#142) */
export function formatDateTime(value: string | Date, tz?: string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(i18n.language || undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });
}

/** Localised short date-time WITH year - "Apr 25, 2026, 2:42 PM".
 *  Use on long-lived surfaces (Timeline, audit logs, archived
 *  payments) where the row may be re-read months later and the year
 *  is the disambiguator. ``formatDateTime`` (year-less) stays for
 *  short-lived in-flight contexts where today/yesterday is implicit. */
export function formatDateTimeWithYear(value: string | Date, tz?: string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(i18n.language || undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  });
}

/** Localised long date - "April 25, 2026".
 *  Pass ``tz`` to render in the location's timezone. (#142) */
export function formatDate(value: string | Date, tz?: string): string {
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(i18n.language || undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  });
}

/** Sanitise a free-typed amount string into a normalised decimal.
 *  Allows digits + at most one decimal point + at most 2 decimals.
 *  Strips leading zeros / extraneous chars. */
export function sanitizeAmount(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  if (parts.length === 1) return parts[0] ?? "";
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
}

/** Parse a sanitised amount string into integer cents. Returns null
 *  on invalid / non-positive inputs. */
export function parseAmountToCents(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const num = Number.parseFloat(trimmed);
  if (!Number.isFinite(num) || num <= 0) return null;
  // Round to nearest cent. Float arithmetic is fine for parsing only;
  // we never store the float.
  return Math.round(num * 100);
}
